// La bibliothèque des drills rangée par usage (D-179, point 1) : cinq rayons fixes, un usage par drill. Proposé d'office
// selon son type (fractionné → Physique, signes → Lecture du jeu, circuit → Échauffement, réaction → Réaction), changeable
// d'un toucher. La base fait la même chose (`_drill_default_usage`, migration 20261108400000) pour les drills d'avant et
// ceux enregistrés sans usage : les deux doivent rester d'accord (testé).
import type { DrillKind } from './drills';
import type { Category } from './session';

export type Usage = 'echauffement' | 'reaction' | 'lecture' | 'tir' | 'physique';
export const USAGES: Usage[] = ['echauffement', 'reaction', 'lecture', 'tir', 'physique'];

export interface UsageInfo {
  label: string;
  /** Les couleurs des catégories des séances (D-043) ; Tir : un bleu canard à lui. */
  color: string;
  /** L'icône (drills-jeux.css : `.dj-i-<icon>`). */
  icon: 'flame' | 'bolt' | 'eye' | 'target' | 'run';
  /** Une ligne sous le titre de la page du rayon. */
  text: string;
  /** La catégorie de l'élément quand on l'ajoute à une séance. */
  category: Category;
}
export const USAGE: Record<Usage, UsageInfo> = {
  echauffement: { label: 'Échauffement', color: '#E0A526', icon: 'flame', text: 'Pour lancer la séance : se mettre en route, le corps et la tête.', category: 'echauffement' },
  reaction: { label: 'Réaction', color: '#2B5FB8', icon: 'bolt', text: 'Un signal à l’écran, on réagit : flèche, couleur, chiffre, prénom.', category: 'technique' },
  lecture: { label: 'Lecture du jeu', color: '#6D4BC2', icon: 'eye', text: 'Lire, décider, annoncer : les signes de l’équipe, les consignes.', category: 'tactique' },
  tir: { label: 'Tir', color: '#0E7490', icon: 'target', text: 'Des circuits de tir : les positions, le temps, la fatigue.', category: 'technique' },
  physique: { label: 'Physique', color: '#C2410C', icon: 'run', text: 'Effort et récup : fractionnés, sprints, gainage.', category: 'physique' },
};

/** Un usage relu sans confiance (base, adresse) : null s'il n'en est pas un. */
export const readUsage = (v: unknown): Usage | null => (typeof v === 'string' && (USAGES as string[]).includes(v) ? v as Usage : null);

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, unknown> : {});
/** Le drill tire-t-il des signes de système (au hasard, ou dans une brique de sa séquence) ? */
export function usesSigns(config: unknown): boolean {
  const c = obj(config);
  if ('signs' in obj(c.stimuli)) return true;
  const steps = obj(c.sequence).steps;
  return Array.isArray(steps) && steps.some(s => 'signs' in obj(obj(s).stimuli));
}

/** L'usage proposé d'office (le même que la base). */
export function defaultUsage(kind: DrillKind, config: unknown): Usage {
  if (kind === 'fractionne') return 'physique';
  if (kind === 'circuit') return 'echauffement';
  return usesSigns(config) ? 'lecture' : 'reaction';
}

/** L'usage d'un drill : le sien, sinon celui proposé d'office (un drill lu avant la migration). */
export const usageOf = (d: { kind: DrillKind; config: unknown; usage?: unknown }): Usage => readUsage(d.usage) ?? defaultUsage(d.kind, d.config);

/** D'où vient un drill dans la bibliothèque : à toi (créé ou gardé), du club, prêt à l'emploi d'Arceau. */
export type Source = 'toi' | 'club' | 'pret';

export interface ShelfCount { total: number; toi: number; club: number; pret: number }
/** Les compteurs d'un rayon (« 5 · 4 à toi · 1 prêt »). */
export function shelfCounts(items: { usage: Usage; source: Source }[]): Record<Usage | 'tout', ShelfCount> {
  const zero = (): ShelfCount => ({ total: 0, toi: 0, club: 0, pret: 0 });
  const out = { tout: zero(), ...Object.fromEntries(USAGES.map(u => [u, zero()])) } as Record<Usage | 'tout', ShelfCount>;
  for (const it of items) for (const k of [it.usage, 'tout'] as const) { out[k].total++; out[k][it.source]++; }
  return out;
}
/** « 4 à toi · 1 prêt », « 1 à toi · 1 du club », « 3 prêts » ; rien : « rien encore ». */
export function shelfLine(c: ShelfCount): string {
  const p: string[] = [];
  if (c.toi) p.push(`${c.toi} à toi`);
  if (c.club) p.push(`${c.club} du club`);
  if (c.pret) p.push(`${c.pret} prêt${c.pret > 1 ? 's' : ''}`);
  return p.join(' · ') || 'rien encore';
}

/**
 * L'ordre d'un rayon : le drill proposé au club par le responsable technique (★) en tête, puis les tiens, ceux du club,
 * les prêts à l'emploi ; à égalité, l'ordre reçu (le plus récent d'abord).
 */
export function shelfOrder<T extends { source: Source; proposed: boolean }>(items: T[]): T[] {
  const rank = (x: T) => (x.proposed ? 0 : x.source === 'toi' ? 1 : x.source === 'club' ? 2 : 3);
  return items.map((x, i) => ({ x, i })).sort((a, b) => rank(a.x) - rank(b.x) || a.i - b.i).map(y => y.x);
}
